import React, { useState, useEffect, ReactNode, ReactElement, CSSProperties } from 'react'
import {
  Modal,
  Spin,
  message as AntMessage,
  notification as AntNotification,
  Empty,
  Button,
} from 'antd'
import { Loading3QuartersOutlined } from '@ant-design/icons'
import { t } from 'i18next'

import { Initial } from 'react-initial'
import { renderToStaticMarkup } from 'react-dom/server'
import i18n from 'i18n'
import classNames from 'classnames'

import conf from 'conf'

import NoInvitationIcon from 'assets/images/icon-no-invitation.svg'
import NoContactIcon from 'assets/images/contacts/no-contacts.svg'
import threadWriteIcon from 'assets/images/icon-thread-write.svg'

import { timeFormat as timeFormatOrigin } from 'utils'
import {
  isBizErrorCode,
  BIZ_ERROR_CODES_ENABLED,
  getBizErrorMessageKey,
  getBizErrorMessageFallback,
} from 'services/bizError'
import { showBizErrorModal, setBizErrorModalRenderer } from 'services/bizError/modal'
import { setFunctionPopupCloser } from 'services/popupCleanup'

import parse from 'html-react-parser'

import syncAniGIF from 'assets/images/gif/sync-ani.gif'

// import sortAscendSVG from 'assets/images/sort-ascend.svg'
// import sortDescendSVG from 'assets/images/sort-descend.svg'
import xCircleRed22SVG from 'assets/images/exclamation-circle-red-22.svg'
import xTriangleRed22SVG from 'assets/images/exclamation-triangle-red-22.svg'
import iconClose20SVG from 'assets/images/icon-close-20.svg'
import SearchEmptyIcon from 'assets/images/icon-search-gray-24.svg'
import RecentFolderEmptyIcon from 'assets/images/icon-no-folder-64.svg'
import NoMessageIcon from 'assets/images/icon-empty-thread.svg'
import styles from './style.module.scss'

// window.moment = moment

// const getUTCTime = (time) => {
//   let utcTime = time
//   if (time != null && time.length > 0 && time.indexOf('Z') === -1) {
//     utcTime = `${time}Z`
//   }
//   return utcTime
// }

export function getHighlightedText(str: string | null, highlightText?: string): ReactNode {
  const txt = str != null ? str : ''
  if (highlightText == null || highlightText.length === 0) {
    return str
  }

  const lowertxt = txt.toLowerCase()
  const lowerHtxt = highlightText.toLowerCase()
  const pos = lowertxt.indexOf(lowerHtxt)

  if (pos >= 0) {
    const preText = txt.substring(0, pos)
    const hText = txt.substring(pos, pos + highlightText.length)
    const nextText = txt.substring(pos + highlightText.length)

    return (
      <span>
        {preText}
        <span className="highlight">{hText}</span>
        {nextText}
      </span>
    )
  }

  return <span>{str}</span>
}

export const basicColors: string[] = [
  '#AC725E',
  '#D06B64',
  '#F83A22',
  '#FA573C',
  '#FF7537',
  '#FFAD46',
  '#42D692',
  '#16A765',
  '#7BD148',
  '#B3DC6C',
  '#FBE983',
  '#FAD165',
  '#92E1C0',
  '#9FE1E7',
  '#9FC6E7',
  '#4986E7',
  '#9A9CFF',
  '#B99AFF',
  '#C2C2C2',
  '#CABDBF',
  '#CCA6AC',
  '#F691B2',
  '#CD74E6',
  '#A47AE2',
]

export const basicColorNames = (): string[] => {
  return [
    t('calendar.color.Cocoa', { defaultValue: 'Cocoa' }),
    t('calendar.color.Flamingo', { defaultValue: 'Flamingo' }),
    t('calendar.color.Tomato', { defaultValue: 'Tomato' }),
    t('calendar.color.Tangerine', { defaultValue: 'Tangerine' }),
    t('calendar.color.Pumpkin', { defaultValue: 'Pumpkin' }),
    t('calendar.color.Mango', { defaultValue: 'Mango' }),
    t('calendar.color.Eucalyptus', { defaultValue: 'Eucalyptus' }),
    t('calendar.color.Basil', { defaultValue: 'Basil' }),
    t('calendar.color.Pistachio', { defaultValue: 'Pistachio' }),
    t('calendar.color.Avocado', { defaultValue: 'Avocado' }),
    t('calendar.color.Citron', { defaultValue: 'Citron' }),
    t('calendar.color.Banana', { defaultValue: 'Banana' }),
    t('calendar.color.Sage', { defaultValue: 'Sage' }),
    t('calendar.color.Peacock', { defaultValue: 'Peacock' }),
    t('calendar.color.Cobalt', { defaultValue: 'Cobalt' }),
    t('calendar.color.Blueberry', { defaultValue: 'Blueberry' }),
    t('calendar.color.Lavender', { defaultValue: 'Lavender' }),
    t('calendar.color.Wisteria', { defaultValue: 'Wisteria' }),
    t('calendar.color.Graphite', { defaultValue: 'Graphite' }),
    t('calendar.color.Birch', { defaultValue: 'Birch' }),
    t('calendar.color.Radicchio', { defaultValue: 'Radicchio' }),
    t('calendar.color.Cherry Blossom', { defaultValue: 'Cherry Blossom' }),
    t('calendar.color.Grape', { defaultValue: 'Grape' }),
    t('calendar.color.Amethyst', { defaultValue: 'Amethyst' }),
  ]
}

export const colors: string[] = [
  '#A9624A',
  '#D06B64',
  '#F83A22',
  '#FA573C',
  '#FF7537',
  '#FF950F',
  '#00BB65',
  '#008247',
  '#43A608',
  '#6BBC00',
  '#FFA600',
  '#FFBC0A',
  '#67AC8F',
  '#61BCC4',
  '#7AA4C8',
  '#4986E7',
  '#6D6FDE',
  '#8562D6',
  '#8D8D8D',
  '#AB8E92',
  '#A7767D',
  '#F46594',
  '#F46594',
  '#7446B7',
]

export function stringToHash(s: string | null): number {
  let h = 0
  if (s != null) {
    for (let i = 0; i < s.length; i++) {
      h = (Math.imul(31, h) + s.charCodeAt(i)) | 0
    }
  }
  return h
}

export function getColorByString(str: string): string {
  const absHash = Math.abs(stringToHash(str))
  return colors[Math.abs(((absHash % colors.length) - 1) % colors.length)]
}

function getPureText(name?: string | null, email?: string | null, emptyName?: string): string {
  let tmp: string[] = []
  if (name != null && name.length > 0) {
    tmp = name.split(/[\s\r\n,.(){}\[\]_\+\-]+/gm)
  } else if (email != null && email.length > 0) {
    tmp = email.split(/[\s\r\n,.(){}\[\]_@\+\-]+/gm)
  } else if (emptyName != null) {
    tmp.push(emptyName)
  }

  let str = ''
  tmp.forEach((txt) => {
    if (/^(\p{L}|\d)/gu.test(txt) === true) {
      str += txt.charAt(0)
    }
  })

  return str
}

interface InitialAvatarProps {
  name?: string
  color?: string | null
  seed?: number
  charCount?: number
  textColor?: string
  height?: number
  width?: number
  fontSize?: string | number
  fontWeight?: number
  fontFamily?: string
  radius?: number
  useWords?: boolean
  emptyName?: string
}

export function getInitialAvatar(name?: string | null, email?: string | null): ReactNode | null {
  if (name == null && email == null) {
    return null
  }

  const avatarProps: InitialAvatarProps = {
    // charCount: 2,
    charCount: 1,
    textColor: '#ffffff',
    height: 40,
    width: 40,
    fontSize: 18,
    fontWeight: 700,
    fontFamily:
      'Pretendard GOV Variable, Pretendard GOV, -apple-system, BlinkMacSystemFont, system-ui, Roboto, "Helvetica Neue", "Segoe UI", "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif',
    radius: 0,
  }

  let str = getPureText(name, email)
  str = str.toUpperCase()

  if (str.length === 0) {
    str = '#'
  }

  avatarProps.name = str

  // const seed = 0
  // avatarProps.color = colors[Math.floor((str.charCodeAt(0) + seed) % colors.length)]

  avatarProps.color = getColorByString(name || email || '')

  // Ensure fontSize is a number for react-initial library
  const propsForInitial = {
    ...avatarProps,
    fontSize: typeof avatarProps.fontSize === 'number' ? avatarProps.fontSize : 18,
  }

  return <Initial {...propsForInitial} />
}

function unicodeCharAt(str: string, index: number): string {
  const first = str.charCodeAt(index)
  let second

  if (first >= 0xd800 && first <= 0xdbff && str.length > index + 1) {
    second = str.charCodeAt(index + 1)

    if (second >= 0xdc00 && second <= 0xdfff) {
      return str.substring(index, index + 2)
    }
  }

  return str[index]
}

function unicodeSlice(str: string, start: number, end: number, words?: boolean): string {
  let accumulator = ''
  let character
  let stringIndex = 0
  let unicodeIndex = 0
  let nextSpace = -1
  const { length } = str

  // Remove any leading/trailing spaces
  str = str.trim()

  while (stringIndex < length) {
    character = unicodeCharAt(str, stringIndex)

    if (unicodeIndex >= start && unicodeIndex < end) {
      accumulator += character
    } else {
      break
    }

    stringIndex += character.length
    unicodeIndex += 1

    // Find the next space offset from the previous finding
    nextSpace = words ? str.indexOf(' ', nextSpace + 1) : -1
    stringIndex = nextSpace > 0 ? nextSpace + 1 : stringIndex
  }

  return accumulator
}

export function getInitialAvatarSrcByProps(props: InitialAvatarProps): string {
  const ownProps: InitialAvatarProps = {
    name: 'Name',
    color: null,
    seed: 0,
    charCount: 1,
    textColor: '#ffffff',
    height: 40,
    width: 40,
    fontSize: '18px',
    fontWeight: 700,
    fontFamily:
      'Pretendard GOV Variable, Pretendard GOV, -apple-system, BlinkMacSystemFont, system-ui, Roboto, "Helvetica Neue", "Segoe UI", "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif',
    radius: 0,
    ...props,
  }

  const {
    width,
    height,
    textColor,
    fontFamily,
    fontSize,
    fontWeight,
    charCount,
    useWords,
    name,
    color,
    seed,
    radius: borderRadius,
  } = ownProps

  let initial = unicodeSlice(name || 'Name', 0, charCount || 1, useWords || false).toUpperCase()
  let backgroundColor =
    color != null
      ? color
      : colors[Math.floor((initial.charCodeAt(0) + (seed || 0)) % colors.length)]

  if ((name || '').length === 0) {
    initial = ''
    ;[backgroundColor] = colors
  }

  const InitialSvg = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      pointerEvents="none"
      {...{
        width,
        height,
        style: {
          width,
          height,
          backgroundColor,
          borderRadius,
        },
      }}
    >
      <text
        y="50%"
        x="50%"
        dy="0.36em"
        pointerEvents="auto"
        fill={textColor}
        fontFamily={fontFamily}
        textAnchor="middle"
        style={{
          fontSize,
          fontWeight,
          // fontStretch: 'semi-expanded'
        }}
      >
        {initial}
      </text>
    </svg>
  )

  const encodedStr = encodeURIComponent(renderToStaticMarkup(<InitialSvg />)).replace(
    /%([0-9A-F]{2})/g,
    (match, p1) => {
      return String.fromCharCode(parseInt(`0x${p1}`, 16))
    },
  )

  const imgStr = window.btoa(encodedStr)
  // const imgStr = window.btoa(unescape(encodeURIComponent(renderToStaticMarkup(<InitialSvg />))));
  const svgHtml = `data:image/svg+xml;base64,${imgStr}`

  return svgHtml
}

export function getInitialAvatarSrc(
  name?: string | null,
  email?: string | null,
  props?: InitialAvatarProps,
): string | null {
  const avatarProps: InitialAvatarProps = {
    ...props,
    // charCount: 2,
    charCount: 1,
  }

  if (props == null || props.name == null) {
    if (name == null && email == null && props?.emptyName == null) {
      return null
    }

    let str = getPureText(name, email, props?.emptyName)
    str = str.toUpperCase()

    if (str.length === 0) {
      str = '#'
    }

    avatarProps.name = str
  }

  const emptyName = props != null && props.emptyName != null ? props.emptyName : ''

  if (avatarProps.color == null) {
    avatarProps.color = getColorByString(name || email || emptyName)
  }

  return getInitialAvatarSrcByProps(avatarProps)
}

export function getTotalPeopleElem(count: number): ReactNode {
  const totalStr =
    count === 1
      ? (i18n.t as any)('common.total.one', { count: `<span class='count'>${count}</span>` })
      : (i18n.t as any)('common.total.more', { count: `<span class='count'>${count}</span>` })

  const totalElem = parse(`<span class='total'>${totalStr}</span>`)
  return totalElem
}

export function getTotalUnitElem(count: number): ReactNode {
  const totalStr =
    count === 1
      ? (i18n.t as any)('common.totalunit.one', { count: `<span class='count'>${count}</span>` })
      : (i18n.t as any)('common.totalunit.more', { count: `<span class='count'>${count}</span>` })

  const totalElem = parse(`<span class='total'>${totalStr}</span>`)
  return totalElem
}

export function getTotalAccountElem(count: number): ReactNode {
  const totalStr =
    count === 1
      ? (i18n.t as any)('common.totalaccount.one', { count: `<span class='count'>${count}</span>` })
      : (i18n.t as any)('common.totalaccount.more', {
          count: `<span class='count'>${count}</span>`,
        })

  const totalElem = parse(`<span class='total'>${totalStr}</span>`)
  return totalElem
}

// export function sortIconFunc({sortOrder}) {
//   const icon = (sortOrder === 'ascend') ? (
//       <img src={sortAscendSVG} alt='ascend' />
//     ) : (
//       <img src={sortDescendSVG} alt='descend' />
//     )
//   return icon;
// }

export function timeFormat(timeStr: string | Date, formatStr?: string): string {
  const format = formatStr || 'YYYY.MM.DD HH:mm'
  return timeFormatOrigin(timeStr as any, format)
}

interface ConfirmOptions {
  wrapClassName?: string
  className?: string
  icon?: ReactNode
  closeIcon?: ReactNode
  title?: ReactNode | null
  content: ReactNode
  okText?: string
  cancelText?: string
  closable?: boolean
  maskClosable?: boolean
  keyboard?: boolean
  width?: number
  okButtonProps?: any
  cancelButtonProps?: any
  onOk?: (...args: any[]) => any
  onCancel?: (...args: any[]) => any
  footer?: ReactNode | ((props: any, extra: any) => ReactNode)
  afterClose?: () => void
  afterOpenChange?: (open: boolean) => void
  zIndex?: number
  maskTransitionName?: string
  centered?: boolean
  open?: boolean
  closeButtonProps?: any
  destroyOnHidden?: boolean
}

export interface ModalInstance {
  destroy: () => void
  update: (configUpdate: any) => void
  updateBase?: (updateOpts: any) => void
}

// 기능 팝업(confirmBase 로 생성 — 설정/이동/조직도/일정발송/확인 등) 레지스트리. (OMD-27)
// 강제 로그아웃 시 error/warning(errorBase/warningBase, Modal.error/warning) 은 남기고 이들만 일괄로 닫는다.
const functionPopups = new Set<ModalInstance>()

setFunctionPopupCloser(() => {
  functionPopups.forEach((modal) => {
    try {
      modal.destroy()
    } catch {
      // 이미 닫힌 모달의 destroy 는 무시
    }
  })
  functionPopups.clear()
})

export function confirmBase(options: ConfirmOptions): ModalInstance {
  const wrapClassName =
    options.wrapClassName == null
      ? classNames('om-popup-base')
      : classNames('om-popup-base', options.wrapClassName)

  const confirmProps: any = {
    wrapClassName,
    // getContainer: () => {
    //   return document.querySelector('.officemail')
    // },
    className: options.className,
    icon: options.icon != null ? options.icon : null,
    closeIcon:
      options.closeIcon != null ? options.closeIcon : <img src={iconClose20SVG} alt="close-x" />,
    title: options.title != null ? options.title : null,
    content: options.content,
    okText: options.okText || i18n.t('common.button.add'),
    cancelText: options.cancelText || i18n.t('common.button.cancel'),
    closable: options.closable != null ? options.closable : false,
    maskClosable: options.maskClosable === false ? false : true,
    keyboard: options.keyboard != null ? options.keyboard : true,
    width: options.width != null ? options.width : 480,
    okButtonProps: {
      className: classNames('type1', 'h38', 'w100'),
      style: { minWidth: '100px', border: 'none' },
      ...(options != null ? options.okButtonProps : null),
    },
    cancelButtonProps: {
      className: classNames('h38', 'w100'),
      style: { minWidth: '100px' },
      ...(options != null ? options.cancelButtonProps : null),
    },
    onOk: options.onOk,
    onCancel: options.onCancel,
    footer: options.footer,
    afterClose: () => {
      // 레지스트리에서 제거(정상 닫힘 포함) 후 원래 afterClose 실행. (OMD-27)
      functionPopups.delete(modalObj)
      options.afterClose?.()
    },
    zIndex: options.zIndex || 1800,
  }

  const modalObj = Modal.confirm(confirmProps) as ModalInstance
  // 강제 로그아웃 시 일괄로 닫을 수 있도록 기능 팝업 레지스트리에 등록. (OMD-27)
  functionPopups.add(modalObj)

  modalObj.updateBase = (updateOpts: any) => {
    const opts = {
      ...updateOpts,
    }

    if (opts.okButtonProps != null) {
      confirmProps.okButtonProps = {
        ...confirmProps.okButtonProps,
        ...opts.okButtonProps,
      }

      opts.okButtonProps = confirmProps.okButtonProps
    }

    if (opts.cancelButtonProps != null) {
      confirmProps.cancelButtonProps = {
        ...confirmProps.cancelButtonProps,
        ...opts.cancelButtonProps,
      }

      opts.cancelButtonProps = confirmProps.cancelButtonProps
    }

    modalObj.update(opts)
  }

  return modalObj
}

interface Confirm1Options {
  wrapClassName?: string
  title?: ReactNode
  icon?: ReactNode | null
  closable?: boolean
  maskClosable?: boolean
  width?: number
  content: ReactNode
  okText?: string
  cancelText?: string
  okButtonProps?: any
  cancelButtonProps?: any
  onOk?: (...args: any[]) => any
  onCancel?: (...args: any[]) => any
  footer?: ReactNode | ((props: any, extra: any) => ReactNode)
}

export function confirmBase1(options: Confirm1Options): ModalInstance {
  const opts = {
    wrapClassName: options.wrapClassName || classNames('om-popup-1'),
    title: options.title,
    icon: null,
    closable: options.closable,
    maskClosable: options.maskClosable != null ? options.maskClosable : true,
    width: options.width || 578,
    content: options.content,
    okText: options.okText || i18n.t('common.button.ok'),
    cancelText: options.cancelText || i18n.t('common.button.cancel'),
    okButtonProps: options.okButtonProps || { type: 'text' },
    cancelButtonProps: options.cancelButtonProps || { type: 'text' },
    maskTransitionName: 'maskTransitionName',
    onOk: options.onOk,
    onCancel: options.onCancel,
    footer: options.footer,
  }

  return Modal.confirm(opts) as ModalInstance
}

export function confirmDelete(options: ConfirmOptions): ModalInstance {
  return confirmBase({
    maskTransitionName: 'maskTransitionName',
    icon: <img src={xCircleRed22SVG} alt="error" />,
    okText: i18n.t('common.button.delete'),
    ...options,
    okButtonProps: {
      className: classNames('type1', 'h38'),
      style: { minWidth: '100px' },
      ...(options != null ? options.okButtonProps : null),
    },
    cancelButtonProps: {
      className: classNames('h38'),
      style: { minWidth: '100px' },
      ...(options != null ? options.cancelButtonProps : null),
    },
  } as ConfirmOptions)
}

interface ErrorOptions {
  wrapClassName?: string
  title?: ReactNode | null
  closable?: boolean
  maskClosable?: boolean
  width?: number
  content: ReactNode
  okText?: string
  okButtonProps?: any
  onOk?: (...args: any[]) => any
  footer?: ReactNode | ((props: any, extra: any) => ReactNode)
  icon?: ReactNode
  afterClose?: () => void
}

export function errorBase(options: ErrorOptions): ModalInstance {
  const opts: any = {
    ...(options || {}),
    wrapClassName: options.wrapClassName || classNames('om-popup-error-1'),
    title: options.title,
    closable: options.closable,
    maskClosable: options.maskClosable != null ? options.maskClosable : true,
    width: options.width,
    content: options.content,
    okText: options.okText || i18n.t('common.button.ok'),
    okButtonProps: options.okButtonProps || { className: classNames('type1', 'h38', 'w100') },
    maskTransitionName: 'maskTransitionName',
    onOk: options.onOk,
    footer: options.footer,
  }

  if (options.icon !== undefined) {
    opts.icon = options.icon
  } else {
    opts.icon = <img src={xTriangleRed22SVG} alt="error" />
  }

  return Modal.error(opts) as ModalInstance
}

// biz 에러(710/711/712) 안내 모달 렌더러 등록 — 채널1(SSO 로그인 실패)·채널2(런타임 494) 공통. (OMD-27)
// errorBase 를 사용해 앱 공용 에러 모달 스타일 + 기본 아이콘(빨간 느낌표 삼각형)을 그대로 노출한다.
// packages/services 의 handleBizAccessBlocked/checkNotMatchingEmail 은 showBizErrorModal 로 이 렌더러에 위임.
setBizErrorModalRenderer((code: number | string) => {
  errorBase({
    title: i18n.t('biz.error.title', { defaultValue: '로그인 실패' }),
    // content 키는 getBizErrorMessageKey(code) 로 동적 결정(biz.error.contractExpired / contractTerminated / accessDenied).
    // 두 키는 translation.json 에 수동 유지되므로 추출기에서는 이 줄만 건너뛴다.
    /* i18next-extract-disable-next-line */
    content: i18n.t(getBizErrorMessageKey(code), {
      defaultValue: getBizErrorMessageFallback(code),
    }),
    width: 450,
  })

  // [보류] 왼쪽 하단 'OfficeNEXT 로그아웃' 버튼(footer). 추후 사용 시 위 errorBase 호출을 아래 버전으로 교체. (OMD-27)
  // errorBase 기본 OK 버튼(오른쪽)은 유지하고, 좌하단 버튼은 absolute 로 배치한다.
  // const modalRef: { destroy?: () => void } = {}
  // const modal = errorBase({
  //   title: i18n.t('biz.error.title', { defaultValue: '로그인 실패' }),
  //   content: i18n.t(getBizErrorMessageKey(code), {
  //     defaultValue: getBizErrorMessageFallback(code),
  //   }),
  //   width: 450,
  //   footer: (originNode: ReactNode) => (
  //     <>
  //       <Button
  //         className={classNames('h38')}
  //         style={{ position: 'absolute', left: 24, bottom: 20, minWidth: '100px' }}
  //         onClick={() => {
  //           const logoutURL = getOfficeNEXTURL('/logout')
  //           if (logoutURL != null && logoutURL.length > 0) {
  //             window.open(logoutURL)
  //           }
  //           modalRef.destroy?.()
  //         }}
  //       >
  //         {i18n.t('logOut.button.logoutOfficeNext')}
  //       </Button>
  //       {originNode}
  //     </>
  //   ),
  // })
  // modalRef.destroy = modal.destroy
})

interface WarningOptions {
  wrapClassName?: string
  title?: ReactNode
  content: ReactNode
  okText?: string
  okButtonProps?: any
  onOk?: (...args: any[]) => any
  icon?: ReactNode
  message?: ReactNode
}

export function warningBase(options: WarningOptions): ModalInstance {
  let wrapClassName = classNames(styles.popupBase, 'om-popup-base')
  if (options.wrapClassName != null) {
    wrapClassName = classNames(styles.popupBase, 'om-popup-base', options.wrapClassName)
  }
  const props: any = {
    title: options.title,
    wrapClassName,
    content: options.content,
    okText: options.okText || i18n.t('common.button.ok'),
    maskTransitionName: 'maskTransitionName',
    okButtonProps: {
      className: classNames('type1', 'h38', 'w100'),
      style: { minWidth: '100px' },
      size: 'large',
      ...(options != null ? options.okButtonProps : null),
    },
    onOk: options.onOk,
  }
  if (options.icon !== undefined) {
    props.icon = options.icon
  }
  return Modal.warning(props) as ModalInstance
}

export function popupDoc(options: Confirm1Options): ModalInstance {
  return confirmBase1({
    wrapClassName: classNames(styles.docPopup),
    maskTransitionName: 'maskTransitionName',
    closable: true,
    closeIcon: <img src={iconClose20SVG} alt="close-x" />,
    footer: null,
    width: 896,
    ...options,
  } as any)
}

export function openPrivacy(e?: React.MouseEvent): void {
  popupDoc({
    title: i18n.t('common.desc.privacyPolicy'),
    content: (
      <div className={classNames(styles.docScroll)}>
        <iframe
          id="contentFrame"
          title="OfficeMail Privacy Policy"
          src="/static/OfficeMail_privacy_policy_20250325.html"
          marginWidth={0}
          marginHeight={0}
          frameBorder={0}
          width="100%"
          height="100%"
          scrolling="yes"
        />
      </div>
    ),
  })

  if (e != null) {
    e.stopPropagation()
    e.preventDefault()
  }
}

export function openTerms(e?: React.MouseEvent): void {
  popupDoc({
    title: i18n.t('common.desc.termsOfUse'),
    content: (
      <div className={classNames(styles.docScroll)}>
        <iframe
          id="contentFrame"
          title="OfficeMail Privacy Policy"
          src="/static/OfficeMail_terms_use_20250325.html"
          marginWidth={0}
          marginHeight={0}
          frameBorder={0}
          width="100%"
          height="100%"
          scrolling="yes"
        />
      </div>
    ),
  })

  if (e != null) {
    e.stopPropagation()
    e.preventDefault()
  }
}

interface ReactObjMgr {
  reactObjList: any[]
  updateUI: () => void
  forceUpdate: any
  run: (runFunc: (reactObj: any) => void) => void
  addReactObj: (reactObj: any) => void
  removeReactObj: (reactObj: any) => void
}

export function creactReactObjMgr(): ReactObjMgr {
  const reactMgr: ReactObjMgr = {
    reactObjList: [],
    updateUI: () => {
      reactMgr.reactObjList.forEach((reactObj) => {
        if (reactObj.updateState != null) {
          reactObj.updateState()
        } else {
          reactObj.forceUpdate()
        }
      })
    },
    forceUpdate: null,
    run: (runFunc: (reactObj: any) => void) => {
      reactMgr.reactObjList.forEach((reactObj) => {
        runFunc(reactObj)
      })
    },
    addReactObj: (reactObj: any) => {
      if (reactMgr.reactObjList.indexOf(reactObj) < 0) {
        reactMgr.reactObjList.push(reactObj)
      }
    },
    removeReactObj: (reactObj: any) => {
      const pos = reactMgr.reactObjList.indexOf(reactObj)
      if (pos >= 0) {
        reactMgr.reactObjList.splice(pos, 1)
      }
    },
  }

  reactMgr.forceUpdate = reactMgr.updateUI

  return reactMgr
}

interface PromiseMgr {
  resolveList: Array<(data?: any) => void>
  rejectList: Array<(data?: any) => void>
  add: (resolve: (data?: any) => void, reject: (data?: any) => void) => void
  remove: (resolve: (data?: any) => void, reject: (data?: any) => void) => void
  resolveAll: (data?: any) => void
  rejectAll: (data?: any) => void
}

export function createPromiseMgr(): PromiseMgr {
  const promiseMgr: PromiseMgr = {
    resolveList: [],
    rejectList: [],
    add: (resolve: (data?: any) => void, reject: (data?: any) => void) => {
      const { resolveList, rejectList } = promiseMgr
      if (resolveList.indexOf(resolve) < 0) {
        resolveList.push(resolve)
      }
      if (rejectList.indexOf(reject) < 0) {
        rejectList.push(reject)
      }
    },
    remove: (resolve: (data?: any) => void, reject: (data?: any) => void) => {
      const { resolveList, rejectList } = promiseMgr
      const pos1 = resolveList.indexOf(resolve)
      const pos2 = rejectList.indexOf(reject)
      if (pos1 > -1) {
        resolveList.splice(pos1, 1)
      }
      if (pos2 > -1) {
        rejectList.splice(pos2, 1)
      }
    },
    resolveAll: (data?: any) => {
      promiseMgr.resolveList.forEach((resolveFunc) => {
        resolveFunc(data)
      })
      promiseMgr.resolveList = []
      promiseMgr.rejectList = []
    },
    rejectAll: (data?: any) => {
      promiseMgr.rejectList.forEach((rejectFunc) => {
        rejectFunc(data)
      })
      promiseMgr.resolveList = []
      promiseMgr.rejectList = []
    },
  }

  return promiseMgr
}

export function notImplemented(desc?: string): void {
  let msg = 'not implemented.'
  if (desc != null) {
    msg = `not implemented. ( ${desc} )`
  }
  AntMessage.warning(msg)
}

interface DragInfo {
  eventNode: HTMLElement | null
  isDrag: boolean
  prevPos: { x: number; y: number }
  onMouseDown: (e: MouseEvent) => void
  onMouseMove: (e: MouseEvent) => void
  onMouseUp: (e: MouseEvent) => void
  onMouseLeave: (e: MouseEvent) => void
  addEvent: () => void
  removeEvent: () => void
}

interface DragTmpObj {
  modalObj?: any
  dragInfo?: DragInfo
}

export function dragPopup(tmpObj: DragTmpObj): DragInfo {
  tmpObj.dragInfo = {
    eventNode: null,
    isDrag: false,
    prevPos: {
      x: 0,
      y: 0,
    },
    onMouseDown: (e: MouseEvent) => {
      if (e.which === 1) {
        console.log('onMouseDown - ', e.offsetX, e.offsetY)

        if (e.offsetY < 100) {
          tmpObj.dragInfo!.isDrag = true
          tmpObj.dragInfo!.prevPos = {
            x: e.clientX,
            y: e.clientY,
          }
        }
      }
    },
    onMouseMove: (e: MouseEvent) => {
      if (tmpObj.dragInfo!.isDrag) {
        const moveX = e.clientX - tmpObj.dragInfo!.prevPos.x
        const moveY = e.clientY - tmpObj.dragInfo!.prevPos.y

        console.log('onMouseMove - ', moveX, moveY, e)

        const node = tmpObj.dragInfo!.eventNode as HTMLElement

        node.style.left = `${node.offsetLeft + moveX}px`
        node.style.top = `${node.offsetTop + moveY}px`

        tmpObj.dragInfo!.prevPos = {
          x: e.clientX,
          y: e.clientY,
        }
      }
    },
    onMouseUp: (/* e */) => {
      tmpObj.dragInfo!.isDrag = false
    },
    onMouseLeave: (/* e */) => {
      tmpObj.dragInfo!.isDrag = false
    },
    addEvent: () => {
      const node = tmpObj.dragInfo!.eventNode as HTMLElement

      node.addEventListener('mousedown', tmpObj.dragInfo!.onMouseDown)
      node.addEventListener('mousemove', tmpObj.dragInfo!.onMouseMove)
      document.addEventListener('mouseup', tmpObj.dragInfo!.onMouseUp)
      document.addEventListener('mouseleave', tmpObj.dragInfo!.onMouseLeave)
    },
    removeEvent: () => {
      const node = tmpObj.dragInfo!.eventNode as HTMLElement

      node.removeEventListener('mousedown', tmpObj.dragInfo!.onMouseDown)
      node.removeEventListener('mousemove', tmpObj.dragInfo!.onMouseMove)
      document.removeEventListener('mouseup', tmpObj.dragInfo!.onMouseUp)
      document.removeEventListener('mouseleave', tmpObj.dragInfo!.onMouseLeave)
    },
  }

  setTimeout(() => {
    console.log(' - tmpObj.modalObj - ', tmpObj.modalObj)

    const node = document.querySelector('.om-popup-base.moveFolder > .ant-modal') as HTMLElement
    tmpObj.dragInfo!.eventNode = node
    tmpObj.dragInfo!.addEvent()
  }, 300)

  return tmpObj.dragInfo
}

export function checkOpenedWindow(win: Window | null, autoClose?: boolean): void {
  if ((window as any).openedWinMgr == null) {
    ;(window as any).openedWinMgr = {
      list: [],
    }

    const closeChildren = () => {
      ;(window as any).openedWinMgr.list.forEach((item: any) => {
        if (item.win != null && !item.win.closed && item.autoClose !== false) {
          item.win.close()
        }
      })
    }
    // unload는 BFCache 정책으로 reload 시 발화되지 않을 수 있어 pagehide/beforeunload로 보강
    window.addEventListener('pagehide', closeChildren)
    window.addEventListener('beforeunload', closeChildren)
  }

  const idx = (window as any).openedWinMgr.list.findIndex((item: any) => {
    return item.win === win
  })
  if (idx < 0) {
    ;(window as any).openedWinMgr.list.push({ win, autoClose })
    // unload 이벤트는 redirection 시에도 발생하므로 실제 close 여부는 win.closed 폴링으로 판정
    const timer = window.setInterval(() => {
      if (win == null || win.closed) {
        window.clearInterval(timer)
        const i = (window as any).openedWinMgr.list.findIndex((item: any) => item.win === win)
        if (i > -1) {
          ;(window as any).openedWinMgr.list.splice(i, 1)
        }
      }
    }, 5000)
  }
}

interface PopupWindowOptions {
  width?: number
  height?: number
}

export function openPopupWindow(
  winName: string,
  title: string,
  url: string,
  options?: PopupWindowOptions,
): Window | null {
  const props = {
    width: 1024,
    height: 711,
    ...options,
  }
  const windowSize = {
    width: Math.min(window.screen.availWidth, props.width),
    height: Math.min(window.screen.availHeight, props.height),
  }
  const windowLocation = {
    left: (window.screen as any).availLeft + window.screen.availWidth / 2 - windowSize.width / 2,
    top: (window.screen as any).availTop + window.screen.availHeight / 2 - windowSize.height / 2,
  }

  const optionProps: Record<string, string | number> = {
    width: Math.round(windowSize.width),
    height: Math.round(windowSize.height),
    left: Math.round(windowLocation.left),
    top: Math.round(windowLocation.top),
    location: 'no',
    status: 'no',
    scrollbars: 'yes',
  }

  const optionList = Object.entries(optionProps).map(([key, value]) => {
    return `${key}=${value}`
  })

  const opts = optionList.join(',')

  const myWin = window.open(url || '', winName, opts)
  checkOpenedWindow(myWin)

  if (myWin) {
    myWin.document.title = title
    myWin.focus()
  }

  return myWin
}

interface ShowResultOptions {
  key?: string
  actionText?: string
  message?: ReactNode
  content?: ReactNode
  closeAfterAction?: boolean
  closeOnClick?: boolean
  loading?: boolean
  action?: (close: () => void) => void
  onClose?: () => void
  duration?: number
  type?: string
}

interface NotiObj {
  key: string
  actionText: string
  message: ReactNode
  closeAfterAction: boolean
  closeOnClick: boolean
  loading: boolean
  close: () => void
  onClick: (e?: React.MouseEvent) => void
  onClose: () => void
  render: () => ReactNode
}

export function showResult(options: ShowResultOptions): NotiObj {
  const notiObj: NotiObj = {
    key: options.key || `result-${Date.now()}`,
    actionText: options.actionText || '',
    message: options.message || options.content || '',
    closeAfterAction: options.closeAfterAction != null ? options.closeAfterAction : true,
    closeOnClick: options.closeOnClick != null ? options.closeOnClick : true,
    loading: options.loading != null ? options.loading : false,
    close: () => {
      AntNotification.destroy(notiObj.key)
    },
    onClick: (e?: React.MouseEvent) => {
      if (options.action != null) {
        options.action(notiObj.close)
      }
      if (e != null) {
        e.preventDefault()
        e.stopPropagation()
      }
      if (notiObj.closeAfterAction !== false) {
        notiObj.close()
      }
    },
    onClose: () => {
      if (options.onClose != null) {
        options.onClose()
      }
    },
    render: () => <></>,
  }

  notiObj.render = () => {
    let actionElem: ReactNode = null
    if (options.action != null) {
      actionElem = (
        <div className={classNames(styles.action)}>
          <a href="" onClick={notiObj.onClick}>
            {notiObj.actionText}
          </a>
        </div>
      )
    }

    const loadingStyle: CSSProperties = {
      height: '24px',
      fontSize: '24px',
      verticalAlign: 'top',
      marginTop: '-6px',
      marginRight: '10px',
    }

    if (
      notiObj.message == null ||
      (typeof notiObj.message === 'string' && notiObj.message.trim() === '')
    ) {
      delete loadingStyle.marginRight
    }

    const loadingElem = notiObj.loading ? (
      <Spin indicator={<Loading3QuartersOutlined spin />} style={loadingStyle} />
    ) : null

    return (
      <div className={classNames(styles.resultContainer)}>
        <div className={classNames(styles.message)}>
          {loadingElem}
          {notiObj.message}
        </div>
        {actionElem}
      </div>
    )
  }

  const msgOptions: any = {
    icon: null,
    className: classNames(styles.resultPopup),
    message: notiObj.render(),
    description: null,
    closeIcon: false,
    placement: 'bottom',
    key: notiObj.key,
    onClick: () => {
      if (notiObj.closeOnClick !== false) {
        notiObj.close()
      }
    },
    onClose: () => {
      notiObj.onClose()
    },
  }

  // second 단위.
  if (options.duration != null) {
    msgOptions.duration = options.duration
  }

  AntNotification.open(msgOptions)

  return notiObj
}

export function getEmailName(email: string): string {
  const pos = email.indexOf('@')
  return email.substring(0, pos)
}

// const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/
const emailRegex =
  /^[a-zA-Z0-9\+\.\_\%\-\+]{1,256}\@[a-zA-Z0-9][a-zA-Z0-9\-]{0,64}(\.[a-zA-Z0-9][a-zA-Z0-9\-]{1,63})+$/

export function isEmail(emailStr: string): boolean {
  const isEmailValid = emailRegex.test(emailStr.trim())
  return isEmailValid
}

interface SelectionChangeMgr {
  alive: boolean
  listeners: Array<(e: Event) => void>
  run: (e: Event) => void
}

const selectionChangeMgr: SelectionChangeMgr = {
  alive: false,
  listeners: [],
  run: (e: Event) => {
    const { alive, listeners } = selectionChangeMgr
    if (alive === true) {
      listeners.forEach((func) => {
        func(e)
      })
    }
  },
}

export function addSelectionChangeEvent(func: (e: Event) => void): void {
  const { alive, listeners, run } = selectionChangeMgr
  const pos = listeners.indexOf(func)
  if (pos > -1) {
    listeners.splice(pos, 1)
  }
  listeners.push(func)

  if (alive === false && listeners.length > 0) {
    selectionChangeMgr.alive = true
    document.addEventListener('selectionchange', run)
  }
}

export function removeSelectionChangeEvent(func: (e: Event) => void): void {
  const { listeners, alive, run } = selectionChangeMgr
  const pos = listeners.indexOf(func)
  if (pos > -1) {
    listeners.splice(pos, 1)
  }

  if (alive === true && listeners.length === 0) {
    selectionChangeMgr.alive = false
    document.removeEventListener('selectionchange', run)
  }
}

interface LoadingAniOptions {
  text: string
}

export function openloadingAni(options: LoadingAniOptions): ModalInstance {
  const opts = {
    wrapClassName: classNames('loading-ani-popup'),
    maskTransitionName: 'maskTransitionName',
    icon: null,
    width: 384,
    title: null,
    footer: null,
    content: (
      <div className={classNames('loading-content')}>
        <div className={classNames('loading-ani')}>
          <img src={syncAniGIF} alt="sync" />
        </div>
        <div className={classNames('loading-text')}>{options.text}</div>
      </div>
    ),
  }

  return Modal.confirm(opts) as ModalInstance
}

interface Mailbox {
  getRole: () => string
  getName: () => string
  getDisplayName: () => string
  searchText?: string | null
}

interface EmptyDataProps {
  type: string
  mailbox?: Mailbox
}

export const EmptyData: React.FC<EmptyDataProps> = ({ type, mailbox }) => {
  const [msgInfo, setMsgInfo] = useState<any>({})

  useEffect(() => {
    let attrs: any = {}
    switch (type) {
      case 'invitation':
        attrs = {
          image: NoInvitationIcon,
          title: t('calendar.edit.noInvitation'),
          // description: t('record.title.emptyDesc'),
        }
        break
      case 'search':
        attrs = {
          image: SearchEmptyIcon,
          title: t('mailbox.folderMenu.nothingSearchResult'),
        }
        break
      case 'recentFolder':
        attrs = {
          image: RecentFolderEmptyIcon,
          title: t('mailbox.folderMenu.nothingRecentFolder'),
        }
        break
      case 'favoriteFolder':
        attrs = {
          image: RecentFolderEmptyIcon,
          title: t('mailbox.folderMenu.nothingFavoriteFolder'),
        }
        break
      case 'thread': {
        if (mailbox != null) {
          const role = mailbox.getRole()
          console.log(' --- mailbox', role, mailbox.getName())

          let title = ''
          let description = ''

          if (mailbox?.searchText != null) {
            title = t('mailbox.empty.search')
            description = ''
          } else if (role === 'inbox') {
            title = t('mailbox.empty.inbox')
            description = t('mailbox.empty.inboxDesc')
          } else if (role === 'drafts') {
            title = t('mailbox.empty.draft')
            description = t('mailbox.empty.draftDesc')
          } else if (role === 'sent') {
            title = t('mailbox.empty.sent')
            description = t('mailbox.empty.sentDesc')
          } else if (role === 'flagged') {
            title = t('mailbox.empty.flagged')
            description = ''
          } else if (role === 'unread') {
            title = t('mailbox.empty.unread')
            description = ''
          } else {
            title = t('mailbox.empty.etc', { folder: mailbox.getDisplayName() })
            description = ''
          }

          attrs = {
            title: mailbox != null ? title : '',
            description: description,
          }
        }
        break
      }
      case 'contacts':
        attrs = {
          image: NoContactIcon,
          title: t('contact.list.empty'),
        }
        break

      case 'message':
        attrs = {
          image: NoMessageIcon,
          title: 'Start a new message to begin the conversation',
          classname: 'messageEmptyData',
        }
        break
    }

    setMsgInfo(attrs)
  }, [type, mailbox])

  const searchStyle: CSSProperties | undefined = type === 'search' ? { color: '#aaa' } : undefined

  return (
    <Empty
      className={classNames(
        'emptyData',
        msgInfo.classname || (type === 'chatRoom' && 'chatRoomEmptyData'),
      )}
      image={msgInfo.image}
      styles={{
        image: {
          height: '64px',
          width: '64px',
        },
      }}
      description={
        <div
          className={classNames(type !== 'message' && 'messageEmptyData', 'emptyDataDescription')}
        >
          <span style={searchStyle}>{msgInfo.title}</span>
          <span style={searchStyle}>{msgInfo.description}</span>
        </div>
      }
    />
  )
}

interface ResizeObserverObj {
  resizeTimer: number | null
  element: HTMLElement | null
  onResize: ((element: HTMLElement) => void) | null
  observer: ResizeObserver
  setElement: (element: HTMLElement) => void
  calcHeight: () => void
  clear: () => void
  start: () => void
  stop: () => void
}

interface CreateResizeObserverOptions {
  element?: HTMLElement
  onResize?: (element: HTMLElement) => void
}

export function createResizeObserver(options?: CreateResizeObserverOptions): ResizeObserverObj {
  const opts = options || {}

  const observerObj: ResizeObserverObj = {
    resizeTimer: null,
    element: opts.element || null,
    onResize: opts.onResize || null,
    observer: null as any,
    setElement: (element: HTMLElement) => {
      observerObj.stop()
      observerObj.element = element
      observerObj.start()
    },
    calcHeight: () => {
      if (observerObj.resizeTimer != null) {
        cancelAnimationFrame(observerObj.resizeTimer)
      }
      observerObj.resizeTimer = requestAnimationFrame(() => {
        if (observerObj.onResize != null && observerObj.element != null) {
          observerObj.onResize(observerObj.element)
        }
        observerObj.resizeTimer = null
      })
    },
    clear: () => {
      const { observer, resizeTimer } = observerObj

      if (observer != null) {
        observer.disconnect()
        if (resizeTimer != null) {
          cancelAnimationFrame(resizeTimer)
        }
      }
    },
    start: () => {
      if (observerObj.element != null) {
        observerObj.calcHeight()
        observerObj.observer.observe(observerObj.element)
      }
    },
    stop: () => {
      if (observerObj.resizeTimer != null) {
        cancelAnimationFrame(observerObj.resizeTimer)
      }
      if (observerObj.element != null) {
        observerObj.observer.unobserve(observerObj.element)
      }
    },
  }

  observerObj.observer = new ResizeObserver(() => {
    observerObj.calcHeight()
  })

  if (observerObj.element != null) {
    observerObj.start()
  }

  return observerObj
}

export function getOfficeNEXTURL(path?: string): string | null {
  const { officeNextURL } = conf

  if (officeNextURL != null && officeNextURL.length > 0) {
    const lang = i18n.language.substring(0, 2)
    const pathStr = path || '/'
    return `${officeNextURL}/${lang}${pathStr}`
  }

  return null
}

export function checkNotMatchingEmail(e: any) {
  const warning = (options: any) => {
    const props: any = {
      title: options.title,
      content: options.content,
      width: 450,
    }

    if (options.icon !== undefined) {
      props.icon = options.icon
    }

    return errorBase(props)
  }

  // biz 플랜 계약 만료/해지/접근권한 상실(710/711/712) — SSO 로그인 실패 채널.
  // 채널2(런타임 494)와 동일한 안내 모달(title '로그인 실패' + 코드별 문구)로 표시한다.
  // SSO 콜백의 code 는 문자열("710")로 올 수 있어(params.code?: string) 헬퍼가 정규화 처리한다.
  // 환경 게이트로 감싸 운영/스테이징 빌드에서는 정적 제거된다. (OMD-27)
  if (BIZ_ERROR_CODES_ENABLED && isBizErrorCode(e.code)) {
    showBizErrorModal(e.code)
    return
  }

  if (e.code != null && e.code === 605) {
    if (e.message === 'Not matching email') {
      confirmBase({
        title: i18n.t('logOut.title.officeNext'),
        content: i18n.t('login.error.notMatchingEmail'),
        okButtonProps: {
          className: classNames('type1', 'h38'),
          style: { minWidth: '100px' },
        },
        cancelButtonProps: {
          className: classNames('h38'),
          style: { minWidth: '100px' },
        },
        width: 450,
        okText: i18n.t('logOut.button.logoutOfficeNext'),
        cancelText: i18n.t('common.button.close'),
        onOk: () => {
          const logoutURL = getOfficeNEXTURL('/logout')

          if (logoutURL != null && logoutURL.length > 0) {
            window.open(logoutURL)
          }
        },
      })
    } else {
      warning({
        content: e.message,
      })
    }
  } else {
    warning({
      content: e.message,
    })
  }
}

interface ThreadWriteButtonProps {
  func?: () => void
}

export const ThreadWriteButton: React.FC<ThreadWriteButtonProps> = ({ func = () => {} }) => {
  return (
    <Button
      className="threadWrite"
      icon={<img src={threadWriteIcon} alt="threadWriteIcon" />}
      onClick={() => func()}
    />
  )
}

export const showFullScreenLoading = (): void => {
  const loading = document.createElement('div')
  loading.id = 'full-screen-loading'
  loading.style.position = 'fixed'
  loading.style.top = '0'
  loading.style.left = '0'
  loading.style.width = '100%'
  loading.style.height = '100%'
  loading.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'
  loading.style.zIndex = '1001'
  loading.style.display = 'flex'
  loading.style.alignItems = 'center'
  loading.style.justifyContent = 'center'

  const spinner = document.createElement('div')
  spinner.className = 'spinner'
  spinner.style.width = '32px'
  spinner.style.height = '32px'
  spinner.style.border = '4px solid #e0e0e0'
  spinner.style.borderTop = '4px solid #0078ff'
  spinner.style.borderRadius = '50%'
  spinner.style.animation = 'spin 1s linear infinite'
  spinner.style.margin = 'auto'

  const style = document.createElement('style')
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `
  spinner.appendChild(style)
  loading.appendChild(spinner)
  document.body.appendChild(loading)
}

export const hideFullScreenLoading = (): void => {
  const loading = document.getElementById('full-screen-loading')
  if (loading) {
    loading.remove()
  }
}

interface ListObject {
  getPaginationInfo: () => { total: number }
}

export function getTitleWithCount(listObj: ListObject, titleStr: string): ReactElement {
  const pageInfo = listObj.getPaginationInfo()

  const totalStr = pageInfo.total > 0 ? `(${pageInfo.total})` : ''

  const title = (
    <span>
      {titleStr} {totalStr}
    </span>
  )

  return title
}

export function formatBytes(
  bytes: number,
  decimals: number = 2,
  withStyle: boolean = false,
  fixedUnit?: string | number,
  space: string = ' ',
): string | ReactElement {
  // if (bytes === 0) return '0 Bytes';
  let ret: string | ReactElement = '-'
  try {
    if (bytes === 0) return '0'

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

    let i = 0

    if (fixedUnit != null) {
      i = typeof fixedUnit === 'number' ? fixedUnit : sizes.indexOf(fixedUnit)
    } else {
      i = Math.floor(Math.log(bytes) / Math.log(k))
    }

    const num = window.parseFloat((bytes / k ** i).toFixed(dm))

    if (Number.isNaN(i) === false && num != null) {
      if (withStyle === true) {
        ret = (
          <span className={classNames('byte-format')}>
            <span className={classNames('number')}>{num}</span>
            <span className={classNames('space')}>{space}</span>
            <span className={classNames('unit')}>{sizes[i]}</span>
          </span>
        )
      } else {
        ret = `${num}${space}${sizes[i]}`
      }
    }
  } catch {
    ret = '-'
  }

  return ret
}

export class ErrorEx<DataType> extends Error {
  type?: string
  data?: DataType
  msgNode?: ReactNode

  constructor(message: string, type?: string, data?: DataType, msgNode?: ReactNode) {
    super(message)
    this.name = 'ErrorEx'
    this.type = type
    this.data = data
    this.msgNode = msgNode
  }
}

interface HighlightContext {
  pattern: RegExp
  lowerTerms: string[]
  createKey: (suffix?: string | number) => string
}

const splitSearchTerms = (searchQuery: string): string[] => {
  return searchQuery
    .split(' ')
    .map((term) => term.trim())
    .filter(Boolean)
}

const escapeRegExp = (term: string): string => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const createRegex = (terms: string[]): RegExp => {
  const escapedTerms = terms.map(escapeRegExp)
  return new RegExp(`(${escapedTerms.join('|')})`, 'gi')
}

const createKeyFactory = (): ((suffix?: string | number) => string) => {
  let counter = 0
  return (suffix: string | number = '') => {
    counter += 1
    return `highlight-${counter}${suffix !== '' ? `-${suffix}` : ''}`
  }
}

const highlightPlainText = (value: string | number, context: HighlightContext): React.ReactNode => {
  const text = typeof value === 'string' ? value : String(value)
  const parts = text.split(context.pattern)
  let hasMatch = false

  const highlighted = parts
    .map((part, index) => {
      if (part === '') {
        return null
      }

      const isMatch = context.lowerTerms.includes(part.toLowerCase())
      if (isMatch) {
        hasMatch = true
        return <mark key={context.createKey(index)}>{part}</mark>
      }

      return part
    })
    .filter((part) => part !== null && part !== '')

  if (!hasMatch) {
    return value
  }

  return highlighted
}

const RAW_HTML_PROP = `dangerouslyS${'etInnerHTML'}`

const highlightNode = (node: React.ReactNode, context: HighlightContext): React.ReactNode => {
  if (node == null) {
    return node
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return highlightPlainText(node, context)
  }

  if (Array.isArray(node)) {
    return node.map((child) => highlightNode(child, context))
  }

  if (React.isValidElement(node)) {
    if (node.type === 'mark') {
      return node
    }

    const propsObj = (node.props as any) || {}
    const { children } = propsObj
    if (propsObj[RAW_HTML_PROP] || children == null || typeof children === 'function') {
      return node
    }

    const highlightedChildren = highlightNode(children, context)
    if (highlightedChildren === children) {
      return node
    }

    return React.cloneElement(node as any, { children: highlightedChildren })
  }

  return node
}

export const highlightSearchText = (
  text: React.ReactNode,
  searchQuery: string,
): React.ReactNode => {
  if (!searchQuery || text == null) {
    return text
  }

  const searchTerms = splitSearchTerms(searchQuery)

  if (searchTerms.length === 0) {
    return text
  }

  const context: HighlightContext = {
    pattern: createRegex(searchTerms),
    lowerTerms: searchTerms.map((term) => term.toLowerCase()),
    createKey: createKeyFactory(),
  }

  return highlightNode(text, context)
}

export const renderHighlightedText = (
  text: React.ReactNode,
  searchQuery: string,
): React.ReactElement => {
  const highlighted = highlightSearchText(text, searchQuery)

  if (!Array.isArray(highlighted)) {
    return <span>{highlighted}</span>
  }

  return <span>{highlighted}</span>
}

const uiUtil = {
  getColorByString,
  getInitialAvatar,
  getInitialAvatarSrcByProps,
  getInitialAvatarSrc,
  getTotalPeopleElem,
  getTotalUnitElem,
  // sortIconFunc,
  timeFormat,
  confirmDelete,
  openPrivacy,
  openTerms,
  creactReactObjMgr,
  createPromiseMgr,
  notImplemented,
  dragPopup,
  openPopupWindow,
  showResult,
  getEmailName,
  addSelectionChangeEvent,
  removeSelectionChangeEvent,
  isEmail,
  EmptyData,
  createResizeObserver,
  getOfficeNEXTURL,
  ThreadWriteButton,
  getTitleWithCount,
  formatBytes,
  ErrorEx,
}

export default uiUtil
